import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi, ALL_ROLES, tenantAssignableRoles, type User, type SeatsInfo } from '../api/users'
import { useAuthStore } from '../store/authStore'
import { Badge, type BadgeTone } from '../components/Badge'

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('sr-Latn-RS', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

// "Deaktiviran" here, not "Blokiran" -- `active` is the older, separate
// toggle (used elsewhere to pull a user off the active roster; also drives
// Play/TestFlight tester sync). docs/plan-user-blocking.md's real,
// reversible-and-still-seated "is_blocked" gets its own badge below, so the
// two can't be read as the same thing.
function userStatus(u: User): { label: string; color: string } {
  if (!u.active)        return { label: 'Deaktiviran',     color: 'bg-red-100 text-red-700' }
  if (u.is_placeholder) return { label: 'Čeka aktivaciju', color: 'bg-amber-100 text-amber-700' }
  return                       { label: 'Aktivan',         color: 'bg-emerald-100 text-emerald-700' }
}

// Replaces (not supplements) userStatus()'s badge when set -- a bounced/
// complained user is by definition still "Čeka aktivaciju", so showing both
// badges side by side would just double up the same amber "not activated
// yet" signal. `critical` (red) on both sub-statuses is deliberate, not a
// missed distinction: a deactivated user can't also be mid-invite (per
// isoft-admin's own handoff), so this never has to visually compete with
// userStatus()'s red "Deaktiviran" badge in practice. The real "Blokiran"
// badge (is_blocked) is independent of both and rendered alongside, not
// through this function.
function bounceStatus(u: User): { label: string; tone: BadgeTone } | null {
  if (u.email_bounce_status === 'bounced')   return { label: 'Email nije isporučen', tone: 'critical' }
  if (u.email_bounce_status === 'complained') return { label: 'Prijavljeno kao spam', tone: 'critical' }
  return null
}

function roleLabel(role: string) {
  return ALL_ROLES.find((r) => r.value === role)?.label ?? role
}

// ── License bar ───────────────────────────────────────────────────────────────

function LicenseBar({ info }: { info: SeatsInfo }) {
  const pct    = info.seats > 0 ? Math.round((info.used / info.seats) * 100) : 100
  const full   = info.used >= info.seats
  const warn   = pct >= 80

  const barColor = full ? 'bg-red-500' : warn ? 'bg-amber-400' : 'bg-emerald-500'
  const textColor = full ? 'text-red-700' : warn ? 'text-amber-700' : 'text-gray-600'

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Licence</span>
          <span className={`text-sm font-semibold ${textColor}`}>
            {info.used} / {info.seats}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all ${barColor}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
      </div>
      <div className="text-xs text-gray-400 sm:text-right shrink-0 space-y-0.5">
        {info.expires_at && (
          <p>Važi do: <span className="font-medium text-gray-600">{new Date(info.expires_at).toLocaleDateString('sr-Latn-RS')}</span></p>
        )}
        {full && (
          <p className="text-red-600 font-medium">Sve licence su iskorišćene — kontaktirajte I-SOFT</p>
        )}
      </div>
    </div>
  )
}


// ── Role badge ────────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  const tones: Record<string, BadgeTone> = {
    company_admin: 'info',
    admin:         'info',
    executive:     'neutral',
    analyst:       'success',
    dispatcher:    'warning',
    meter_reader:  'teal',
    scada_operator: 'slate',
    scada_repair:   'slate',
  }
  return <Badge tone={tones[role] ?? 'neutral'}>{roleLabel(role)}</Badge>
}

// ── Invite modal ──────────────────────────────────────────────────────────────

function InviteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { user: currentUser } = useAuthStore()
  const availableRoles = tenantAssignableRoles(currentUser?.modules ?? [])
  const [email, setEmail]         = useState('')
  const [fullName, setFullName]   = useState('')
  const [roles, setRoles]         = useState<string[]>([])
  const [error, setError]         = useState('')

  const mutation = useMutation({
    mutationFn: () => usersApi.invite({ email, full_name: fullName || undefined, roles }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      onClose()
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg ?? 'Greška pri slanju pozivnice')
    },
  })

  const toggleRole = (role: string) =>
    setRoles((prev) => prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Pozovi radnika</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" viewBox="0 -960 960 960" fill="currentColor">
              <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="radnik@mionica.rs"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ime i prezime (opciono)</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Petar Petrović"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Role</label>
            <div className="flex flex-wrap gap-2">
              {availableRoles.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleRole(value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    roles.includes(value)
                      ? 'bg-brand-cyan text-brand-midnight border-brand-cyan'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-3 pt-1">
          <button
            onClick={() => mutation.mutate()}
            disabled={!email || mutation.isPending}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight text-sm font-medium py-2 rounded-lg"
          >
            {mutation.isPending ? 'Slanje…' : 'Pošalji pozivnicu'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded-lg"
          >
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Role editor ───────────────────────────────────────────────────────────────

function RoleEditor({
  user,
  currentUserId,
  onDone,
}: {
  user: User
  currentUserId: string
  onDone: () => void
}) {
  const qc = useQueryClient()
  const { user: currentUser } = useAuthStore()
  const availableRoles = tenantAssignableRoles(currentUser?.modules ?? [])
  const [selected, setSelected] = useState<string[]>(user.roles)
  const isSelf = user.user_id === currentUserId

  const mutation = useMutation({
    mutationFn: () => usersApi.assignRoles(user.user_id, selected),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      onDone()
    },
  })

  const toggle = (role: string) => {
    // Prevent self-lockout
    if (isSelf && (role === 'company_admin' || role === 'admin') && selected.includes(role)) return
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    )
  }

  return (
    <div className="mt-3 border border-gray-200 rounded-overlay shadow-overlay p-4 bg-gray-50 space-y-3">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Dodeli role</p>
      <div className="flex flex-wrap gap-2">
        {availableRoles.map(({ value, label }) => {
          const active = selected.includes(value)
          const locked = isSelf && (value === 'company_admin' || value === 'admin') && active
          return (
            <button
              key={value}
              onClick={() => toggle(value)}
              disabled={locked}
              title={locked ? 'Ne možete ukloniti sopstvenu admin rolu' : undefined}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                active
                  ? 'bg-brand-cyan text-brand-midnight border-brand-cyan'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              } ${locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {label}
            </button>
          )
        })}
      </div>
      {selected.length === 0 && (
        <p className="text-xs text-amber-600">Korisnik bez rola neće imati pristup sistemu.</p>
      )}
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-xs font-semibold py-2 rounded-lg disabled:opacity-50"
        >
          {mutation.isPending ? 'Čuvanje…' : 'Sačuvati role'}
        </button>
        <button
          onClick={onDone}
          className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold py-2 rounded-lg"
        >
          Otkazati
        </button>
      </div>
      {mutation.isError && (
        <p className="text-xs text-red-600">Greška. Pokušajte ponovo.</p>
      )}
    </div>
  )
}

// ── Mobile accounts editor ──────────────────────────────────────────────────

function MobileAccountsEditor({ user, onDone }: { user: User; onDone: () => void }) {
  const qc = useQueryClient()
  const [googleAccount, setGoogleAccount] = useState(user.google_account ?? '')
  const [iosAccount, setIosAccount]       = useState(user.ios_account ?? '')
  const [error, setError]                 = useState('')

  const mutation = useMutation({
    mutationFn: () => usersApi.updateProfile(user.user_id, {
      google_account: googleAccount.trim() || undefined,
      ios_account:    iosAccount.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      onDone()
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg ?? 'Greška pri čuvanju')
    },
  })

  return (
    <div className="mt-3 border border-gray-200 rounded-overlay shadow-overlay p-4 bg-gray-50 space-y-3">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Mobilni nalozi</p>
      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Google-konto (Android)</label>
          <input
            type="email"
            value={googleAccount}
            onChange={(e) => setGoogleAccount(e.target.value)}
            placeholder="korisnik@gmail.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Apple ID (iOS)</label>
          <input
            type="email"
            value={iosAccount}
            onChange={(e) => setIosAccount(e.target.value)}
            placeholder="korisnik@icloud.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-xs font-semibold py-2 rounded-lg disabled:opacity-50"
        >
          {mutation.isPending ? 'Čuvanje…' : 'Sačuvati'}
        </button>
        <button
          onClick={onDone}
          className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold py-2 rounded-lg"
        >
          Otkazati
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

// ── User card ─────────────────────────────────────────────────────────────────

function UserCard({ user, currentUserId, scadaEnabled }: { user: User; currentUserId: string; scadaEnabled: boolean }) {
  const qc = useQueryClient()
  const [editingRoles, setEditingRoles] = useState(false)
  const [editingMobile, setEditingMobile] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [blockError, setBlockError] = useState('')
  const isSelf = user.user_id === currentUserId
  const { label, color } = userStatus(user)
  const bounce = bounceStatus(user)
  const bounceAt = fmtDate(user.email_bounce_at ?? null)
  const bounceTitle = bounce && user.email_bounce_type
    ? `${user.email_bounce_type}${bounceAt ? ` — ${bounceAt}` : ''}`
    : undefined
  // docs/plan-security-hardening-1.md §3b: a tenant admin can't touch a
  // director's roles, block status or existence -- the API refuses with
  // 403 either way, this just keeps the UI from inviting the attempt.
  const directorLocked = user.is_director
  const canDelete = user.is_placeholder && !user.activated_at && !isSelf && !directorLocked

  const toggleActive = useMutation({
    mutationFn: () => usersApi.setActive(user.user_id, !user.active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const blockMut = useMutation({
    mutationFn: () => user.is_blocked ? usersApi.unblock(user.user_id) : usersApi.block(user.user_id),
    onSuccess: () => {
      setBlockError('')
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setBlockError(msg ?? 'Greška pri promeni statusa blokiranja')
    },
  })

  const toggleNotifyScada = useMutation({
    mutationFn: () => usersApi.setNotifyScada(user.user_id, !user.notify_scada),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const resendInvite = useMutation({
    mutationFn: () => usersApi.resendInvite(user.user_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const deleteUser = useMutation({
    mutationFn: () => usersApi.deleteUser(user.user_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['seats'] })
    },
  })

  // Avatar initials
  const initials = user.full_name
    ? user.full_name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : (user.email ?? '?')[0].toUpperCase()

  return (
    <div className={`bg-white rounded-xl border p-5 space-y-3 transition-shadow hover:shadow-md ${!user.active || user.is_blocked ? 'opacity-60' : ''}`}>
      {/* Top row */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <span className="text-sm font-bold text-brand-cyan-text">{initials}</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user.full_name ?? <span className="text-gray-400 italic">Bez imena</span>}
            </p>
            {isSelf && <span className="text-xs text-brand-cyan-text font-medium">(vi)</span>}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{user.email ?? 'Nema email'}</p>
        </div>

        <div className="flex flex-wrap gap-1 justify-end shrink-0">
          {user.is_director && (
            <Badge tone="info" title="Nalog direktora -- role, blokiranje i uklanjanje menja samo I-SOFT">
              Direktor
            </Badge>
          )}
          {user.is_blocked && <Badge tone="critical">Blokiran</Badge>}
          {bounce ? (
            <Badge tone={bounce.tone} title={bounceTitle}>
              {bounce.label}
            </Badge>
          ) : (
            <Badge className={color}>
              {label}
            </Badge>
          )}
        </div>
      </div>

      {/* Roles */}
      <div className="flex flex-wrap gap-1.5 min-h-[24px]">
        {user.roles.length > 0
          ? user.roles.map((r) => <RoleBadge key={r} role={r} />)
          : <span className="text-xs text-gray-400 italic">Bez rola</span>
        }
      </div>

      {/* Activation info */}
      {user.activated_at && (
        <p className="text-xs text-gray-400">
          Aktiviran: {fmtDate(user.activated_at)}
        </p>
      )}
      {user.is_placeholder && !user.activated_at && (
        <p className="text-xs text-amber-600">Čeka aktivaciju — link nije iskorišćen</p>
      )}

      {/* Mobile accounts (read-only) */}
      <div className="text-xs text-gray-400 space-y-0.5">
        <p>Google: {user.google_account ?? '—'}</p>
        <p>Apple ID: {user.ios_account ?? '—'}</p>
      </div>

      {/* Role editor */}
      {editingRoles && (
        <RoleEditor
          user={user}
          currentUserId={currentUserId}
          onDone={() => setEditingRoles(false)}
        />
      )}

      {/* Mobile accounts editor */}
      {editingMobile && (
        <MobileAccountsEditor user={user} onDone={() => setEditingMobile(false)} />
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="border border-red-200 bg-red-50 rounded-xl p-3 space-y-2">
          <p className="text-xs text-red-700 font-medium">Ukloniti ovu neaktiviranu pozivnicu?</p>
          <div className="flex gap-2">
            <button
              onClick={() => { deleteUser.mutate(); setConfirmDelete(false) }}
              disabled={deleteUser.isPending}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold py-1.5 rounded-lg disabled:opacity-50"
            >
              {deleteUser.isPending ? '…' : 'Ukloni'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold py-1.5 rounded-lg"
            >
              Otkaži
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1 border-t border-gray-100 flex-wrap">
        {scadaEnabled && (
          <button
            onClick={() => toggleNotifyScada.mutate()}
            disabled={toggleNotifyScada.isPending}
            title={user.notify_scada ? 'SCADA alarmi: Uključeni — kliknite da isključite' : 'SCADA alarmi: Isključeni — kliknite da uključite'}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
              user.notify_scada
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill={user.notify_scada ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Alarmi
          </button>
        )}
        {!directorLocked && (
          <button
            onClick={() => setEditingRoles((e) => !e)}
            className="flex-1 text-xs font-medium text-gray-600 hover:text-brand-cyan-text py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          >
            {editingRoles ? 'Zatvori' : 'Izmeni role'}
          </button>
        )}
        <button
          onClick={() => setEditingMobile((e) => !e)}
          className="flex-1 text-xs font-medium text-gray-600 hover:text-brand-cyan-text py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
        >
          {editingMobile ? 'Zatvori' : 'Mobilni nalozi'}
        </button>
        {user.is_placeholder && !user.activated_at && (
          <button
            onClick={() => resendInvite.mutate()}
            disabled={resendInvite.isPending}
            className="flex-1 text-xs font-medium text-brand-cyan-text hover:text-brand-midnight py-1.5 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
          >
            {resendInvite.isPending ? '…' : 'Ponovi pozivnicu'}
          </button>
        )}
        {canDelete ? (
          <button
            onClick={() => setConfirmDelete((v) => !v)}
            className="flex-1 text-xs font-medium text-red-500 hover:text-red-700 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
          >
            Ukloni
          </button>
        ) : !isSelf && !directorLocked ? (
          <button
            onClick={() => toggleActive.mutate()}
            disabled={toggleActive.isPending}
            className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
              user.active
                ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            {toggleActive.isPending ? '…' : user.active ? 'Deaktivirati' : 'Aktivirati'}
          </button>
        ) : null}
        {!isSelf && !directorLocked && (
          <button
            onClick={() => user.is_blocked ? blockMut.mutate() : setConfirmBlock((v) => !v)}
            disabled={blockMut.isPending}
            className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
              user.is_blocked
                ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                : 'text-red-600 hover:text-red-700 hover:bg-red-50'
            }`}
          >
            {blockMut.isPending ? '…' : user.is_blocked ? 'Odblokiraj' : 'Blokiraj'}
          </button>
        )}
      </div>

      {/* Block confirmation */}
      {confirmBlock && !user.is_blocked && (
        <div className="border border-red-200 bg-red-50 rounded-xl p-3 space-y-2">
          <p className="text-xs text-red-700 font-medium">
            Blokirati ovog korisnika? Gubi pristup prijavi i push obaveštenjima dok se ne odblokira.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => { blockMut.mutate(); setConfirmBlock(false) }}
              disabled={blockMut.isPending}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold py-1.5 rounded-lg disabled:opacity-50"
            >
              {blockMut.isPending ? '…' : 'Blokiraj'}
            </button>
            <button
              onClick={() => setConfirmBlock(false)}
              className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold py-1.5 rounded-lg"
            >
              Otkaži
            </button>
          </div>
        </div>
      )}
      {blockError && <p className="text-xs text-red-600">{blockError}</p>}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const { user: currentUser } = useAuthStore()
  const [filter, setFilter]   = useState<'all' | 'active' | 'pending'>('all')
  const [inviting, setInviting] = useState(false)
  const scadaEnabled = currentUser?.modules?.includes('scada') ?? false

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
    staleTime: 30_000,
  })

  const { data: seats } = useQuery({
    queryKey: ['seats'],
    queryFn: () => usersApi.getSeats().then((r) => r.data),
    staleTime: 60_000,
  })

  const filtered = users.filter((u) => {
    if (filter === 'active')  return u.active && !u.is_placeholder
    if (filter === 'pending') return u.is_placeholder
    return true
  })

  const pendingCount = users.filter((u) => u.is_placeholder).length

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {inviting && <InviteModal onClose={() => setInviting(false)} />}

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Filter tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          {([
            { key: 'all',     label: `Svi (${users.length})` },
            { key: 'active',  label: 'Aktivni' },
            { key: 'pending', label: `Čekaju (${pendingCount})` },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-white text-gray-900 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <button
          onClick={() => setInviting(true)}
          disabled={seats ? seats.used >= seats.seats : false}
          title={seats && seats.used >= seats.seats ? 'Sve licence su iskorišćene' : undefined}
          className="flex items-center gap-2 bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 disabled:cursor-not-allowed text-brand-midnight text-sm font-medium px-4 py-2 rounded-lg"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Pozovi radnika
        </button>
      </div>

      {/* License bar */}
      {seats && <LicenseBar info={seats} />}

      {/* Cards grid */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-10 h-10 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm">Nema korisnika</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((u) => (
            <UserCard
              key={u.user_id}
              user={u}
              currentUserId={currentUser?.id ?? ''}
              scadaEnabled={scadaEnabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}
